import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { memo, useCallback } from "react";
import { Button, Chip, IconButton, Stack, TextField, Typography } from "@mui/material";
import { SelectData } from "mui-rff";
import { FieldFormCheckboxRow } from "../FieldFormCheckboxRow";
import { FieldTypeEditorProps } from "../types";

type SelectDataRow = string | SelectData;

function isSelectOptionObject(item: unknown): item is { label: string; value: string | number } {
  return (
    typeof item === "object"
    && item !== null
    && "label" in item
    && "value" in item
    && typeof (item as { label: unknown }).label === "string"
  );
}

export const SelectFieldTypeSpecificProps = memo(
  ({ field, onChange }: FieldTypeEditorProps) => {
    const updateProp = useCallback(
      <K extends keyof typeof field>(key: K, value: typeof field[K]) => {
        onChange({ ...field, [key]: value });
      },
      [field, onChange],
    );

    const setDataAt = useCallback(
      (index: number, value: string) => {
        const currentData = (field.data ?? []) as SelectDataRow[];
        const nextData = currentData.map((item, itemIndex) =>
          itemIndex === index && typeof item === "string" ? value : item,
        );
        onChange({ ...field, data: nextData as typeof field.data });
      },
      [field, onChange],
    );

    const removeDataAt = useCallback(
      (index: number) => {
        const currentData = (field.data ?? []) as SelectDataRow[];
        const nextData = currentData.filter((_, itemIndex) => itemIndex !== index);
        onChange({
          ...field,
          data: nextData.length ? (nextData as typeof field.data) : undefined,
        });
      },
      [field, onChange],
    );

    const addDataString = useCallback(() => {
      const currentData = (field.data ?? []) as SelectDataRow[];
      onChange({ ...field, data: [...currentData, ""] as typeof field.data });
    }, [field, onChange]);

    return (
      <Stack spacing={1.5} sx={{ pt: 0.5 }}>
        <FieldFormCheckboxRow
          label='Allow multiple'
          checked={!!field.multiple}
          onChange={(checked) => updateProp("multiple", checked)}
        />
        <Typography variant='caption' color='text.secondary'>
          Options
        </Typography>
        <Stack spacing={1}>
          {((field.data ?? []) as SelectDataRow[]).map((item, index) => (
            typeof item === "string" ? (
              <Stack key={index} direction='row' spacing={1} alignItems='center'>
                <TextField
                  size='small'
                  fullWidth
                  value={item}
                  onChange={(e) => setDataAt(index, e.target.value)}
                  placeholder='Option'
                />
                <IconButton
                  aria-label='Remove option'
                  size='small'
                  onClick={() => removeDataAt(index)}
                >
                  <DeleteIcon fontSize='small' />
                </IconButton>
              </Stack>
            ) : (
              <Stack key={index} direction='row' spacing={1} alignItems='center'>
                <Chip
                  size='small'
                  variant='outlined'
                  label={
                    isSelectOptionObject(item)
                      ? `${item.label} (${String(item.value)})`
                      : JSON.stringify(item)
                  }
                />
                <IconButton
                  aria-label='Remove option'
                  size='small'
                  onClick={() => removeDataAt(index)}
                >
                  <DeleteIcon fontSize='small' />
                </IconButton>
              </Stack>
            )
          ))}
        </Stack>
        <Button size='small' startIcon={<AddIcon />} onClick={addDataString}>
          Add option
        </Button>
        <TextField
          label='Other option value'
          helperText='Value from options that opens freeform input'
          value={field.other ?? ""}
          onChange={(e) => updateProp("other", e.target.value || undefined)}
          size='small'
          fullWidth
        />
      </Stack>
    );
  },
);
